import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { format } from "date-fns"
import { XmppContext } from "@/stream/xmppClient"
import AdHocCommands from "@/api/adHocCommands"
import { parseFuelReport } from "./fuelReportParser"
import type { DriverFuelRow } from "./fuelReportTypes"
import i18n from "@/lib/i18n/i18n"

export type FuelRange = { from: Date; to: Date }

function buildDateRange(range: FuelRange): { start: string; end: string } {
  const start = new Date(range.from)
  start.setHours(0, 0, 0, 0)
  const end = new Date(range.to)
  end.setHours(23, 59, 59, 999)
  return { start: start.toISOString(), end: end.toISOString() }
}
function dateKey(date: Date): string {
  return format(date, "yyyy-MM-dd")
}

function rangeKey(range: FuelRange, driverJid?: string): string {
  return `${dateKey(range.from)}..${dateKey(range.to)}|${driverJid ?? "all"}`
}

type CacheEntry = { rows: DriverFuelRow[]; fetchedAt: number }

// Past days never change; today keeps accruing events, so its cache entry goes
// stale and triggers a background refresh after this long.
const TODAY_STALE_MS = 2 * 60 * 1000

/** Is this a cache entry whose range reaches today (or later) that has
 * outlived its freshness window? Past-only ranges are immutable. */
export function isStaleTodayEntry(
  key: string,
  fetchedAt: number,
  now: number,
): boolean {
  const to = key.split("|")[0].split("..")[1]
  return to >= dateKey(new Date(now)) && now - fetchedAt > TODAY_STALE_MS
}

export function useFuelReportData(range: FuelRange, driverJid?: string) {
  const { client } = useContext(XmppContext)
  const [drivers, setDrivers] = useState<DriverFuelRow[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const cacheRef = useRef<Map<string, CacheEntry>>(new Map())
  // Monotonic request id: only the latest request may touch state, so a slow
  // response for a previously-selected day can't overwrite the current one.
  const requestSeqRef = useRef(0)

  const fetchData = useCallback(
    async (forceRefresh = false) => {
      if (!client) return
      const seq = ++requestSeqRef.current
      const isCurrent = () => seq === requestSeqRef.current
      const key = rangeKey(range, driverJid)
      const cached = forceRefresh ? undefined : cacheRef.current.get(key)
      if (cached) {
        // Clear any error left by a previously-failed day — otherwise the
        // cached table stays hidden behind a stale error banner.
        setError(null)
        setIsLoading(false)
        setDrivers(cached.rows)
        // Past days are immutable; a stale "today" refreshes in the background
        // (the cached rows stay on screen meanwhile).
        if (!isStaleTodayEntry(key, cached.fetchedAt, Date.now())) return
      }
      setIsLoading(true)
      setError(null)
      try {
        const { start, end } = buildDateRange(range)
        const result = await AdHocCommands.getFuelReport(
          client,
          start,
          end,
          driverJid,
        )
        const parsed = await parseFuelReport(result)
        cacheRef.current.set(key, { rows: parsed, fetchedAt: Date.now() })
        if (isCurrent()) setDrivers(parsed)
      } catch (err) {
        console.error("Error fetching fuel report:", err)
        if (isCurrent()) {
          setError(
            err instanceof Error
              ? err.message
              : i18n.t("reports.fuelReport.fetchFailed"),
          )
        }
      } finally {
        if (isCurrent()) setIsLoading(false)
      }
    },
    // Depend on the formatted days (stable primitives) rather than the raw
    // `range` object: callers commonly pass a freshly-constructed range on
    // every render, and a reference-based dependency here would recreate
    // `fetchData` (and re-trigger the effect below) on every render even
    // when the calendar days haven't changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, dateKey(range.from), dateKey(range.to), driverJid],
  )

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const refetch = useCallback(() => fetchData(true), [fetchData])

  return { drivers, isLoading, error, refetch }
}
